import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const DEFAULT_OVERVIEW_TOP = [
  "To inspire every child to think fearlessly, learn with curiosity, lead with confidence and grow with compassion.",
  "At St. Andrews World School, our vision is to foster a truly holistic approach to education where academic excellence, emotional well-being, strong values, creativity and future-ready skills come together to shape well-rounded individuals.",
  "We aspire to nurture confident, responsible and empathetic learners who are equipped to embrace a rapidly changing world, make thoughtful choices and contribute meaningfully to society.",
  "Our vision is to make every child's school journey a strong foundation for lifelong learning, leadership, character and meaningful success."
];

const DEFAULT_OVERVIEW_BOTTOM = [
  "Shared values give our schools a clear educational direction. Integrity, respect, compassion and intellectual curiosity guide our expectations of learning, conduct and relationships. We encourage professional reflection, collaboration and continued growth among our educators, strengthening a community committed to thoughtful teaching and responsible mentorship.",
  "Our ambition extends beyond success in school: we prepare children for life. Critical thinking, adaptability and ethical judgement equip learners to make considered decisions, pursue evolving careers and navigate a world shaped by artificial intelligence and emerging technologies with confidence.",
  "Emotional security is inseparable from educational excellence. Our educators build relationships founded on empathy and trust, encouraging children to express concerns, seek reassurance and approach difficulties with renewed self-belief. Open dialogue with parents deepens our understanding of each learner and strengthens the support they receive.",
  "Across St. Andrews, we seek to provide an education in which individual promise is recognised, ambition finds direction and every child develops the confidence to participate, contribute and belong."
];

const DEFAULT_MISSION_PARAS = [
  "At St. Andrews World School, our mission is to foster a holistic educational environment where academic excellence, emotional well-being, character, and future-ready skills unite to shape compassionate global leaders.",
  "We provide experiential and inquiry-driven learning that empowers learners to explore, question, innovate, and develop lifelong curiosity.",
  "Through dedicated faculty mentorship and world-class infrastructure, we ensure that every student's unique potential is recognized, cultivated, and celebrated."
];

const DEFAULT_VISION_PARAS = [
  "At St. Andrews Group, we aspire to cultivate forward-thinking environments across all our campuses where curiosity is nurtured and individual potential is fully realized.",
  "By seamlessly blending academic excellence with character development, we envision empowering students to navigate evolving global landscapes, embrace technological advancements, and contribute meaningfully to society.",
  "We aim to set a benchmark in education where every learner emerges as a resilient, reflective, and principled global citizen prepared for future challenges."
];

const DEFAULT_PHILOSOPHY_PARAS = [
  "St. Andrews World School's educational philosophy is to provide child-centric education through our uniquely crafted academics. Our modern schooling education equally focuses on the interactive and experiential part of learning.",
  "We want to impart knowledge that can help them with real-world situations and not be just limited to the classroom. We have adopted modern schooling education where we work towards promoting the academic success of the student. We ensure that their every achievement is celebrated and they feel motivated.",
  "St. Andrews World School dedicatedly works toward holistic development. Our core values work in synchronization with our philosophy of 'Education for all' — emphasizing the success of students.",
  "St. Andrews World School has a student-centric curriculum, where the teachers and parents equally work for the student's academic prowess."
];

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const renderParagraphs = (raw, fallbackArray = [], pClassName = '') => {
  const cls = pClassName ? ` ${pClassName}` : '';
  if (!raw) {
    return fallbackArray.map((p, i) => <p key={i} className={cls.trim()}>{p}</p>);
  }
  const parts = String(raw).replace(/\r/g, '').split(/\n\s*\n|\n/).map(s => s.trim()).filter(Boolean);
  if (!parts.length) {
    return fallbackArray.map((p, i) => <p key={i} className={cls.trim()}>{p}</p>);
  }
  return parts.map((p, i) => <p key={i} className={cls.trim()}>{p}</p>);
};

const AboutGroup = () => {
  const pageData = usePage('about-standrews-group');
  const sections = pageData.sections || {};

  const overview = sections.overview || {};
  const mission = sections.mission || {};
  const vision = sections.vision || {};
  const philosophy = sections.philosophy || {};

  const overviewHeading = overview.heading || "Seven Branches. A Shared Purpose. A Commitment to Every Child.";
  const overviewKicker = overview.kicker || "Overview";
  const overviewLeadQuote = overview.lead_quote || "“Education’s finest legacy is a generation with the courage to question, the integrity to lead and the compassion to serve.”";
  const overviewQuoteBanner = overview.quote_banner || "“An education of lasting value develops the intellect to question, the courage to persevere and the integrity to lead.”";

  const missionHeading = mission.heading || "Our Mission";
  const missionLeadQuote = mission.lead_quote || "To inspire every child to think fearlessly, learn with curiosity, lead with confidence and grow with compassion.";

  const visionHeading = vision.heading || "Our Vision";
  const visionLeadQuote = vision.lead_quote || "Our vision is to make every child's school journey a strong foundation for lifelong learning, leadership, character and meaningful success in a dynamic world.";

  const philosophyHeading = philosophy.heading || "Philosophy";

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || "About The Group";
  const bannerSubtitle = banner.subtitle || "Pioneers in holistic education and character building.";
  const bannerBreadcrumb = banner.breadcrumb || "ABOUTGROUP";
  const bannerImgSrc = pageData.bannerImage || '/images/page-banners/about-us.jpg';

  return (
    <>
      <SEO slug="about-standrews-group" title={pageData.title || "About St Andrews World Group"}
        description="Seven Branches. A Shared Purpose. A Commitment to Every Child. Discover the vision, mission, and philosophy of St. Andrews World Group."
        keywords="About St Andrews Group, St Andrews World School, SAWS, Education for All, Seven Branches"
      />

      {/* Top Banner with bottom-centered text matching reference design */}
      <section className="hero-banner relative w-full h-[200px] sm:h-[280px] md:h-[360px] lg:h-[440px] overflow-hidden bg-[#0c1a30]">
        {!pageData.loading && (
          <img
            src={bannerImgSrc}
            alt={bannerTitle}
            className="w-full h-full object-fill object-center"
            loading="eager"
          />
        )}

        {/* Dark Vignette / Gradient Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25"
          aria-hidden="true"
        />

        {/* Bottom-Centered Text Container */}
        <div className="absolute inset-0 flex flex-col justify-end items-center text-center pb-4 sm:pb-7 md:pb-9 lg:pb-11 px-4 z-10">
          <Reveal className="w-full max-w-4xl mx-auto flex flex-col items-center text-center">
            {/* Main Title in Serif */}
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-[54px] font-serif font-normal md:font-medium text-white tracking-normal drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)] leading-tight text-center">
              {bannerTitle}
            </h1>

            {/* Subtitle / Tagline */}
            {bannerSubtitle && (
              <p className="mt-1 sm:mt-2 md:mt-3 text-[11px] sm:text-base md:text-lg lg:text-[19px] text-white/95 font-sans font-normal max-w-3xl mx-auto drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center !text-center">
                {bannerSubtitle}
              </p>
            )}

            {/* Breadcrumb Navigation */}
            <div className="mt-2 sm:mt-3.5 md:mt-5 flex items-center justify-center gap-1.5 text-[10px] sm:text-[13px] uppercase tracking-wider text-white/80 font-medium select-none">
              <Link to="/" className="hover:text-white transition-colors">
                HOME
              </Link>
              <span className="text-white/60 mx-1">&rsaquo;</span>
              <span className="font-bold text-white tracking-widest">
                {bannerBreadcrumb}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 1: OVERVIEW */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto w-full lg:w-[75%] lg:max-w-[75%] px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 w-full">
              <Reveal>
                <div className="mb-8">
                  <span className="text-[13px] md:text-[14px] font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-3 block">
                    {overviewKicker}
                  </span>
                  <h3 className="text-3xl text-[#1b3160] md:text-4xl font-bold leading-tight">
                    {overviewHeading}
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-6" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-5 text-gray-600 leading-relaxed text-[17px]">
                  {overviewLeadQuote && (
                    <p className="font-semibold text-[#1b3160] text-lg">
                      {overviewLeadQuote}
                    </p>
                  )}
                  {renderParagraphs(overview.paragraphs_top, DEFAULT_OVERVIEW_TOP)}
                </div>
              </Reveal>
            </div>

            {/* Right Image */}
            <Reveal delayMs={70} className="order-1 lg:order-2">
              <div className="relative p-4 border border-gray-200 bg-[#f9f8f4] shadow-sm">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img
                    src={resolveImage(overview.image, '/images/aboutus.jpeg')}
                    alt={overviewHeading}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/page-banners/about-us.jpg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#1b3160] hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-university text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>
          </div>

          {/* Lower text block */}
          <Reveal delayMs={150}>
            <div className="mt-12 space-y-5 text-gray-600 leading-relaxed text-[17px]">
              {renderParagraphs(overview.paragraphs_bottom, DEFAULT_OVERVIEW_BOTTOM)}
            </div>
          </Reveal>

          {/* Quote Banner */}
          {overviewQuoteBanner && (
            <div className="mt-16 text-center">
              <div className="p-8 bg-[#f9f8f4] border-l-4 border-[#d8a85f] shadow-sm max-w-4xl mx-auto">
                <p className="italic font-medium text-2xl text-[#1b3160]">
                  {overviewQuoteBanner}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2: OUR MISSION */}
      <section className="bg-[#f9f8f4] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Image */}
            <Reveal delayMs={70} className="order-1">
              <div className="relative p-4 border border-gray-200 bg-white shadow-sm">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img
                    src={resolveImage(mission.image, '/images/page-banners/why-choose-us.jpg')}
                    alt={missionHeading}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/aboutus.jpeg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#1b3160] hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-bullseye text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>

            {/* Right Content */}
            <div className="order-2 w-full">
              <Reveal>
                <div className="mb-8">
                  <h3 className="text-3xl text-[#1b3160] md:text-4xl font-bold leading-tight">
                    {missionHeading}
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-6" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-5 text-gray-600 leading-relaxed text-[17px]">
                  {missionLeadQuote && (
                    <p className="font-semibold text-[#1b3160] text-lg">
                      {missionLeadQuote}
                    </p>
                  )}
                  {renderParagraphs(mission.paragraphs, DEFAULT_MISSION_PARAS)}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: OUR VISION */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 w-full">
              <Reveal>
                <div className="mb-8">
                  <h3 className="text-3xl text-[#1b3160] md:text-4xl font-bold leading-tight">
                    {visionHeading}
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-6" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-5 text-gray-600 leading-relaxed text-[17px]">
                  {visionLeadQuote && (
                    <p className="font-semibold text-[#1b3160] text-lg">
                      {visionLeadQuote}
                    </p>
                  )}
                  {renderParagraphs(vision.paragraphs, DEFAULT_VISION_PARAS)}
                </div>
              </Reveal>
            </div>

            {/* Right Image */}
            <Reveal delayMs={70} className="order-1 lg:order-2">
              <div className="relative p-4 border border-gray-200 bg-[#f9f8f4] shadow-sm">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img
                    src={resolveImage(vision.image, '/images/page-banners/about-school.jpg')}
                    alt={visionHeading}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/Computer-lab-scaled.jpg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#1b3160] hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-eye text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 4: PHILOSOPHY */}
      <section className="bg-[#f9f8f4] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Image */}
            <Reveal delayMs={70} className="order-1">
              <div className="relative p-4 border border-gray-200 bg-white shadow-sm">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img
                    src={resolveImage(philosophy.image, '/images/library.JPG')}
                    alt={philosophyHeading}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/CORE-VALUES-1500x430.jpg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#1b3160] hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-lightbulb text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>

            {/* Right Content */}
            <div className="order-2 w-full">
              <Reveal>
                <div className="mb-8">
                  <h3 className="text-3xl text-[#1b3160] md:text-4xl font-bold leading-tight">
                    {philosophyHeading}
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] mt-6" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-5 text-gray-600 leading-relaxed text-[17px]">
                  {renderParagraphs(philosophy.paragraphs, DEFAULT_PHILOSOPHY_PARAS)}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutGroup;
